import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, notification, Form, Modal, Select, Space,Tooltip,Result ,Input} from "antd";
import GenericTable from "../components/GenericTable";
import GenericDrawer from "../components/GenericModal";
import GenericViewModal from "../components/GenericViewModal";
import {
  fetchIcdsRequest,
  createIcdRequest,
  updateIcdRequest,
  deleteIcdRequest,
  fetchIcdRulesRequest,
  mapIcdToRuleRequest,
  deleteIcdRuleMappingRequest
} from "../redux-saga/actions/icdActions";
import ProjectNavbar from "../components/ProjectNavbar";
import { useParams } from "react-router-dom";
import { fetchRulesRequest } from "../redux-saga/actions/ruleActions";
import moment from "moment";
import { updateCurrentProjectId } from "../redux-saga/actions/customerActions";
import { EyeOutlined, EditOutlined, DeleteOutlined,FileSearchOutlined } from "@ant-design/icons";
import {IconClipboardList} from '@tabler/icons-react';

const { Option } = Select;

const icdModalInputs = [
  { label: "ICD ID", name: "icdId", type: "text" },
  { label: "Code", name: "code", type: "text" },
  { label: "Description", name: "description", type: "text" },
  { label: "Effective Date", name: "effectiveDate", type: "date" },
  { label: "Deleted Date", name: "deletedDate", type: "date" }
];

const IcdPage = () => {
  const dispatch = useDispatch();
  const icds = useSelector((state) => state.icd.icds?.data);
  const rules = useSelector((state) => state.icd.rules?.data);
  const commonRules = useSelector((state) => state.rule.rules?.data);
  const {projectId}=useParams()
  const loading = useSelector((state) => state.icd.loading);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingIcd, setEditingIcd] = useState(null);
  const [form] = Form.useForm();
  const [viewingIcd, setViewingIcd] = useState(null);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isRulesModalVisible, setIsRulesModalVisible] = useState(false);
  const [selectedIcd, setSelectedIcd] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);

  const metaData = useSelector((state) => state.icd.icds?.meta || {});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchIcdsRequest({ page: currentPage, limit: 2, search: searchTerm }));
    dispatch(fetchRulesRequest(projectId))
    dispatch(updateCurrentProjectId(projectId));
  }, [dispatch,currentPage, searchTerm]);

  const handleCreateOrEdit = (values) => {
    const payload = {
      ...values,
      effectiveDate: values.effectiveDate ? values.effectiveDate.toISOString() : null,
      deletedDate: values.deletedDate ? values.deletedDate.toISOString() : null,
    };
    if (editingIcd) {
      dispatch(updateIcdRequest(editingIcd.id, payload));
    } else {
      dispatch(createIcdRequest(payload));
    }
    notification.success({ message: "Success", description: "ICD saved successfully." });
    setIsModalVisible(false);
    setEditingIcd(null);
    form.resetFields();
  };

  const handleView = (record) => {
    const formattedRecord = {
      ...record,
      effectiveDate:record.effectiveDate ? moment(record.effectiveDate).format("YYYY-MM-DD HH:mm:ss") : null,
      deletedDate:record.deletedDate ? moment(record.deletedDate).format("YYYY-MM-DD HH:mm:ss") : null,
      createdAt: record.createdAt ? moment(record.createdAt).format("YYYY-MM-DD HH:mm:ss") : null,
      updatedAt: record.updatedAt ? moment(record.updatedAt).format("YYYY-MM-DD HH:mm:ss") : null,
    };
    setViewingIcd(formattedRecord);
    setIsViewModalVisible(true);
  };

  const handleEdit = (record) => {
    const formattedRecord = {
      ...record,
      effectiveDate: record.effectiveDate ? moment(record.effectiveDate) : null,
      deletedDate: record.deletedDate ? moment(record.deletedDate) : null,
    };
    setEditingIcd(formattedRecord);
    setIsModalVisible(true);
    form.setFieldsValue(formattedRecord);
  };
  

  const handleDelete = (record) => {
   Modal.confirm({
      title: "Are you sure you want to delete this ICD?",
      onOk: () => {
        dispatch(deleteIcdRequest(record.id));
        notification.success({ message: "Success", description: "ICD deleted successfully." });
      },
    });
  };

  // Fetch rules associated with an ICD
  const handleViewRules = (record) => {
    setViewingIcd(record);
    setSelectedIcd(record.id);
    dispatch(fetchIcdRulesRequest(projectId, record.id));
    setIsRulesModalVisible(true);
  };

  // Map a selected rule to the selected ICD
  const handleMapRule = () => {
    if (selectedRule) {
      dispatch(mapIcdToRuleRequest(projectId, viewingIcd.id, selectedRule));
      notification.success({ message: "Success", description: "Rule mapped to ICD successfully." });
      setSelectedRule(null);
      dispatch(fetchIcdRulesRequest(projectId, viewingIcd.id));
    } else {
      notification.warning({ message: "Warning", description: "Please select a rule to map." });
    }
  };

  // Delete a rule mapping
  const handleDeleteRuleMapping = (ruleId) => {
    dispatch(deleteIcdRuleMappingRequest(projectId, viewingIcd.id, ruleId));
    notification.success({ message: "Success", description: "Rule mapping deleted successfully." });
     dispatch(fetchIcdRulesRequest(projectId, viewingIcd.id));
  };

  const columnsConfig = [
    { title: "ICD ID", dataIndex: "icdId" },
    { title: "Code", dataIndex: "code" },
    { title: "Description", dataIndex: "description" },
    { title: "Effective Date", dataIndex: "effectiveDate" },
    { title: "Deleted Date", dataIndex: "deletedDate" },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="View">
            <EyeOutlined style={{ fontSize: "20px", color: "#1890ff" }} onClick={() => handleView(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <EditOutlined style={{ fontSize: "20px", color: "#faad14" }} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteOutlined style={{ fontSize: "20px", color: "#f5222d" }} onClick={() => handleDelete(record)} />
          </Tooltip>
          <Tooltip title="View Rule">
          <FileSearchOutlined style={{ fontSize: "20px", color: "#f5222d" }} onClick={() => handleViewRules(record)} />  
      </Tooltip>
        </Space>
      ),
    },
  ];


  const ruleColumnsConfig = [
    { title: "Rule ID", dataIndex: "ruleId" },
    { title: "Content", dataIndex: "content" },
    {
      title: "Actions",
      render: (_, rule) => (
        <Button onClick={() => handleDeleteRuleMapping(rule.id)}>Delete Mapping</Button>
      ),
    },
  ];

  return (
    <div style={{display:"flex",gap:"10px"}}> 
      <ProjectNavbar />
      <div style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
            <IconClipboardList style={{ fontSize: "24px", color: "black" }} />
            <h2 style={{ margin: 0 }}>ICD</h2>
          </div>

          <div style={{ display: "flex", justifyContent: "end", marginBottom: 11 }}>
            <Button type="primary" onClick={() => setIsModalVisible(true)}>
              Create ICD
            </Button>
          </div>
        </div>
        <Input
          placeholder="Search ICD"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginBottom: "16px" }}
        />
      <GenericTable data={icds} columnsConfig={columnsConfig} loading={loading} pagination={{
            current: metaData.page || 1,
            pageSize: metaData.limit || 2,
            total: metaData.total || 0,
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              dispatch(fetchIcdsRequest({ page, limit: pageSize, search: searchTerm }));
            },
          }} />
      
      {/* ICD Create/Edit Drawer */}
      {isModalVisible && (
          <GenericDrawer
            isVisible={isModalVisible}
            handleCancel={() => {
              setIsModalVisible(false);
              setEditingIcd(null);
            }}
            title={
              <span style={{display : "flex",alignItems:"center"}}>
                <IconClipboardList style={{ color: "white", marginRight: "8px" }} />
                {editingIcd ? "Edit ICD" : "Create ICD"}
              </span>
            }
            inputs={icdModalInputs}
            onSubmit={handleCreateOrEdit}
            predefinedData={editingIcd || {}}
          />
        )
      }
      {/* ICD View Modal */}
      {isViewModalVisible && (
        <GenericViewModal
          isVisible={isViewModalVisible}
          handleCancel={() => setIsViewModalVisible(false)}
          title="View ICD"
          data={viewingIcd}
          footerButtons={["Close"]}
          onButtonClick={() => setIsViewModalVisible(false)}
        />
      )}

      {/* Rules Modal */}
      <Modal
        title="ICD Rules"
        visible={isRulesModalVisible}
        onCancel={() => setIsRulesModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsRulesModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
<div style={{display:"flex",gap:"10px"}}>
<Select
          style={{ width: "100%", marginTop: 16,marginBottom:"10px" }}
          placeholder="Select Rule to Map"
          onChange={(value) => setSelectedRule(value)}
        >
          
          {commonRules?.map((rule) => (
            <Option key={rule.id} value={rule.id}>
              {rule.ruleId}
            </Option>
          ))}
        </Select>
        <Button type="primary" style={{ marginTop: 16 }} onClick={handleMapRule}>
          Map Selected Rule
        </Button>
        </div>
        <GenericTable data={rules} columnsConfig={ruleColumnsConfig} loading={loading} />
        
      
      </Modal>
      </div>
    </div>
  );
};

export default IcdPage;
